import React, { useState, useEffect } from "react";

import {
    Box,
    Divider,
    TextField,
    Grid,
    Switch,
    FormControlLabel,
    styled,
    Card,
    Select,
    MenuItem,
    FormControl,
    FormHelperText,
    InputLabel,
    Checkbox,
    ListItemText,
    OutlinedInput,
    Chip,
    List,
    ListItem, InputAdornment
} from "@mui/material";

import { LoadingButton } from '@mui/lab';
import { useNavigate } from "react-router-dom"
import { useFormik } from 'formik';

import CommonBreadcumbs from '../../common/CommonBreadcumbs';
import CommonSectionHeader from "../../common/CommonSectionHeader";
import CommonRestricted from '../../common/CommonRestricted';
import DatePickerComponent from "../../common/DatePickerComponent";

import { useAppContextFeatures } from '../../../context/AppContext';
import { mergeSchema } from "../../../utils/dataSchema"

import { useTransactions, targetTypes, actionTypes, payloadTypes, destinationTypes } from '../../../hooks/api/useTransactions';
import { useWallets, targetTypes as walletTargetTypes } from '../../../hooks/api/useWallets';
import { useSystemRoutes, routeTypes } from '../../../hooks/useSystemRoutes';

import { useTranslations } from '../../../hooks/useTranslations';
import { useSystemPrompt } from "../../../hooks/useSystemPrompt";

import {
    initialValues,
    validationSchema,
    initialToUserIdValues,
    validationToUserIdSchema
} from "../../../schema/transaction/transactionSchema";

import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from '../../../hooks/usePermissions';
import { CommonIcons, icons } from "../../common/CommonIcons";

import SearchCustomers from "../../dialog/SearchCustomers";

const StyledCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(2)
}));

export default function NewWalletRequest({
    routeType
}) {
    const { t } = useTranslations({ ns: ["common", "@transaction"] })

    const { promptMessage } = useSystemPrompt();
    const { setSystemLoading } = useAppContextFeatures()

    const { isLoading, create } = useTransactions({ targetType: targetTypes.request });
    const { list: listWalletUsers, filterOperatorTypes } = useWallets({ targetType: walletTargetTypes.walletUser });

    const { isPermissionAllow } = usePermissions(routeType, scopeIds.wallet.id, scopeTypes.wallet.wallet.id)

    const navigate = useNavigate();
    const { getRoute } = useSystemRoutes(routeType);
    const { getHtmlHead } = useSystemHead(routeType)

    const [actionTypeInfo, setActionTypeInfo] = useState()
    // const [userId, setUserId] = useState()
    const [fromAvailableWallets, setFromAvailableWallets] = useState([])
    const [toAvailableWallets, setToAvailableWallets] = useState([])

    const getSchema = () => {
        if (actionTypeInfo === actionTypes.request.transfer.id) {
            return mergeSchema(validationSchema, validationToUserIdSchema)
        }
        return validationSchema
    }

    const fetchWalletUsers = async ({ userId, currency = '', isToWallet = false }) => {

        setSystemLoading(true)

        try {

            let filterValues = [
                {
                    name: "user_id",
                    operator: filterOperatorTypes.eq,
                    value: userId
                }
            ]

            if (currency !== "") {
                filterValues.push({
                    name: "wallet_currency",
                    operator: filterOperatorTypes.eq,
                    value: currency
                })
            }

            const props = {
                limit: -1,
                skip: 0,
                filterValue: filterValues,
                sortInfo: {
                    id: "id",
                    dir: 0
                }
            }

            const { data } = await listWalletUsers(props)

            let dataWalletUsers = data.wallet_users

            if (isToWallet) {
                setToAvailableWallets(dataWalletUsers)
            } else { // from
                setFromAvailableWallets(dataWalletUsers)
            }


        } catch (error) {
            promptMessage(false, `${error?.message}`)
        }

        setSystemLoading(false)
    }

    const onSubmit = async (currentValues) => {

        setSystemLoading(true)

        try {

            const {
                actionType: _actionType,
                fromWalletUserId,
                toWalletUserId,
                description,
                transactionAmount,
                referenceNo,
                remark,
                internalReferenceNo,
                internalRemark,
            } = currentValues

            let payloadType = {}
            let destinationPayload = {}

            if (_actionType === actionTypes.request.topup.id) {
                payloadType = payloadTypes.request.topup
            }

            if (_actionType === actionTypes.request.withdraw.id) {
                payloadType = payloadTypes.request.withdraw
            }

            if (_actionType === actionTypes.request.transfer.id) {
                payloadType = payloadTypes.request.transfer

                destinationPayload = {
                    target: destinationTypes.request.transfer.target,
                    module: destinationTypes.request.transfer.module,
                    transaction_ref: destinationTypes.request.transfer.transactionRef,
                    ref_id: {
                        [destinationTypes.request.transfer.refFieldName]: toWalletUserId
                    }
                }
            }

            let transactionPayload = {
                source: {
                    target: payloadType.sourceTarget,
                    module: payloadType.sourceModule,
                    transaction_ref: payloadType.transactionRef,
                    ref_id: {
                        [payloadType.sourceRefFieldName]: fromWalletUserId
                    }
                },
                action: payloadType.action,
                description,
                transaction_amount: transactionAmount,
                transaction_date: currentValues?.transactionDate.format("YYYY-MM-DD"),
                reference_no: referenceNo,
                remark,
                internal_reference_no: internalReferenceNo,
                internal_remark: internalRemark
            }

            transactionPayload = { ...transactionPayload, ...destinationPayload }

            console.log('transactionPayload', transactionPayload)

            let data = {
                transaction: transactionPayload
            }

            const response = await create({ data });

            promptMessage(true, `${response?.message}`)
            navigate(getRoute("transactions/walletRequests"))

        } catch (error) {
            promptMessage(false, `Action Failed. (${error?.message})`)
        }

        setSystemLoading(false)

    }

    const formik = useFormik({
        initialValues,
        validationSchema: getSchema(),
        enableReinitialize: true,
        onSubmit
    });

    const { values, errors, touched, handleChange, setFieldValue } = formik;

    const RenderForm = () => {

        let _actionTypes = Object.keys(actionTypes?.request).map(i => {
            return (
                actionTypes?.request[i]
            )
        })

        return (

            <form onSubmit={formik.handleSubmit}>
                <StyledCard>
                    <Grid container spacing={0.5}>

                        <Grid item xs={12} sm={12} md={2.5} textAlign={"left"}>
                            <CommonSectionHeader
                                title={t('walletRequestInfo')}
                                subTitle={t('detailsOfWalletRequest')}
                            />
                        </Grid>

                        <Grid item xs={12} sm={12} md={9.5}>
                            <Grid container spacing={1}>

                                <Grid item xs={12} sm={12} md={6}>
                                    <FormControl
                                        margin="dense"
                                        fullWidth
                                        size="small"
                                        error={Boolean(errors.actionType)}
                                    >
                                        <InputLabel id="actionType">{t("actionType")}</InputLabel>
                                        <Select
                                            autoFocus
                                            labelId="actionType"
                                            label={t("actionType")}
                                            name={"actionType"}
                                            margin="dense"
                                            onChange={(event) => {
                                                setFieldValue("actionType", event.target.value)
                                                setActionTypeInfo(event.target.value)
                                            }}
                                            value={values.actionType}
                                            sx={{
                                                textAlign: 'left'
                                            }}
                                        >
                                            {
                                                _actionTypes.map((i, index) => {
                                                    return (
                                                        <MenuItem key={index} value={i.id}>{i.display}</MenuItem>
                                                    )
                                                })
                                            }
                                        </Select>
                                        <FormHelperText>
                                            {errors.actionType}
                                        </FormHelperText>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={0} sm={0} md={6}><></></Grid>

                                <Grid item xs={3} sm={3} md={3}>
                                    <SearchCustomers
                                        handleOnCancel={() => {
                                        }}
                                        handleOnSelect={async (props) => {

                                            const { id, item } = props

                                            let userId = item?.user_id

                                            await fetchWalletUsers({ userId, isToWallet: false })

                                            setFieldValue('fromUsername', `${item?.firstname} ${item?.lastname}`)
                                            setFieldValue('fromUserId', item?.user_id)
                                            setFieldValue(`fromWalletUserId`, "")

                                            setFieldValue('toUsername', ``)
                                            setFieldValue('toUserId', "")
                                            setFieldValue(`toWalletUserId`, "")

                                            setToAvailableWallets([])

                                        }}
                                        textProps={{
                                            disabled: true,
                                            value: values.fromUsername,
                                            error: Boolean(errors?.fromUserId),
                                            helperText: errors?.fromUserId,
                                            label: t("fromCustomer")
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={3} sm={3} md={3}>
                                    <FormControl
                                        margin="dense"
                                        fullWidth
                                        size="small"
                                        error={Boolean(errors?.fromWalletUserId)}
                                        sx={{
                                            p: 0,
                                            m: 0
                                        }}
                                    >
                                        <InputLabel id="availableWallet">{t("availableWallet")}</InputLabel>
                                        <Select
                                            labelId="availableWallet"
                                            label={t("availableWallet")}
                                            name={"fromWalletUserId"}
                                            onChange={(e) => {

                                                let id = e.target.value
                                                let _item = fromAvailableWallets.filter(i => i.id === id)
                                                let item = _item.length > 0 ? _item[0] : {}

                                                setFieldValue(`fromWalletUserId`, item?.id)
                                                setFieldValue(`fromWalletCurrency`, item?.wallet_currency)

                                                // reset To
                                                setFieldValue(`toUsername`, "")
                                                setFieldValue(`toWalletUserId`, "")
                                                setToAvailableWallets([])

                                            }}
                                            value={values.fromWalletUserId}
                                            sx={{
                                                textAlign: 'left',
                                                p: 0,
                                                m: 0
                                            }}
                                        >
                                            {
                                                fromAvailableWallets?.map((i, index) => {
                                                    return (
                                                        <MenuItem key={index} value={i.id}>
                                                            {i.wallet_name} ({i.wallet_currency})
                                                        </MenuItem>
                                                    )
                                                })
                                            }
                                        </Select>
                                        <FormHelperText>
                                            {errors?.fromWalletUserId}
                                        </FormHelperText>
                                    </FormControl>
                                </Grid>

                                {
                                    (values?.actionType === actionTypes.request.transfer.id) ?
                                        (
                                            <>
                                                <Grid item xs={12} sm={12} md={3}>
                                                    <SearchCustomers
                                                        handleOnCancel={() => {
                                                        }}
                                                        handleOnSelect={async (props) => {

                                                            const { id, item } = props

                                                            let userId = item?.user_id

                                                            // double check from and to user Id cannot same
                                                            if (userId === values.fromUserId) {
                                                                promptMessage(false, `${t("destinationCannotBeSamePerson")}`)
                                                                return false;
                                                            }

                                                            await fetchWalletUsers({
                                                                userId,
                                                                currency: values.fromWalletCurrency,
                                                                isToWallet: true
                                                            })

                                                            setFieldValue('toUsername', `${item?.firstname} ${item?.lastname}`)
                                                            setFieldValue('toUserId', item?.user_id)
                                                            setFieldValue(`toWalletUserId`, "")

                                                            return true;

                                                        }}
                                                        textProps={{
                                                            disabled: true,
                                                            value: values.toUsername,
                                                            error: Boolean(errors?.toUserId),
                                                            helperText: errors?.toUserId,
                                                            label: t("toCustomer")
                                                        }}
                                                    />
                                                </Grid>

                                                <Grid item xs={12} sm={12} md={3}>
                                                    <FormControl
                                                        margin="dense"
                                                        fullWidth
                                                        size="small"
                                                        error={Boolean(errors?.toWalletUserId)}
                                                        sx={{

                                                            p: 0,
                                                            m: 0
                                                        }}
                                                    >
                                                        <InputLabel id="availableWallet">{t("availableWallet")}</InputLabel>
                                                        <Select
                                                            labelId="availableWallet"
                                                            label={t("availableWallet")}
                                                            name={"toWalletUserId"}
                                                            onChange={handleChange}
                                                            value={values?.toWalletUserId ? values.toWalletUserId : ""}
                                                            sx={{
                                                                textAlign: 'left',
                                                                p: 0,
                                                                m: 0
                                                            }}
                                                        >
                                                            {
                                                                toAvailableWallets?.map((i, index) => {
                                                                    return (
                                                                        <MenuItem key={index} value={i.id}>
                                                                            {i.wallet_name} ({i.wallet_currency})
                                                                        </MenuItem>
                                                                    )
                                                                })
                                                            }
                                                        </Select>
                                                        <FormHelperText>
                                                            {errors?.toWalletUserId}
                                                        </FormHelperText>
                                                    </FormControl>
                                                </Grid>
                                            </>
                                        )

                                        :

                                        (
                                            <Grid item xs={6} sm={6} md={6}>
                                                <></>
                                            </Grid>
                                        )

                                }

                                <Grid item xs={6} sm={6} md={3}>
                                    <TextField
                                        margin="dense"
                                        size="small"
                                        fullWidth
                                        name={"transactionAmount"}
                                        label={t("transactionAmount")}
                                        type="text"
                                        variant="outlined"
                                        value={values.transactionAmount}
                                        onChange={handleChange}
                                        error={Boolean(errors.transactionAmount)}
                                        helperText={touched.transactionAmount && errors.transactionAmount}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} md={3}>
                                    <DatePickerComponent
                                        config={{
                                            name: "transactionDate",
                                            label: t("transactionDate")
                                        }}
                                        formik={{
                                            values,
                                            errors,
                                            touched,
                                            handleChange,
                                            setFieldValue
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={12} md={6}>
                                    <TextField
                                        margin="dense"
                                        size="small"
                                        fullWidth
                                        name={"referenceNo"}
                                        label={t("referenceNo")}
                                        type="text"
                                        variant="outlined"
                                        value={values.referenceNo ? values.referenceNo : ""}
                                        onChange={handleChange}
                                        error={touched.referenceNo && Boolean(errors.referenceNo)}
                                        helperText={touched.referenceNo && errors.referenceNo}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={12} md={6}>
                                    <TextField
                                        margin="dense"
                                        size="small"
                                        fullWidth
                                        name={"description"}
                                        label={t("description")}
                                        type="text"
                                        multiline
                                        rows={2}
                                        variant="outlined"
                                        value={values.description}
                                        onChange={handleChange}
                                        error={touched.description && Boolean(errors.description)}
                                        helperText={touched.description && errors.description}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} md={6}>
                                    <TextField
                                        margin="dense"
                                        size="small"
                                        fullWidth
                                        name={"remark"}
                                        label={t("remark")}
                                        type="text"
                                        multiline
                                        rows={2}
                                        variant="outlined"
                                        value={values.remark}
                                        onChange={handleChange}
                                        error={touched.remark && Boolean(errors.remark)}
                                        helperText={touched.remark && errors.remark}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </StyledCard>
                <Grid container spacing={1} sx={{ pl: 2, pr: 2 }}>
                    <Grid item md={12} textAlign={"right"}>
                        <LoadingButton
                            type="submit"
                            // fullWidth
                            variant="contained"
                            sx={{
                                mt: 3,
                                mb: 2
                            }}
                            loading={isLoading}
                        >
                            {t('saveChanges')}
                        </LoadingButton>
                    </Grid>
                </Grid>
            </form>

        )
    }

    return (
        <Box>
            {
                getHtmlHead({ routeType, title: headTitles.newWalletRequest })
            }

            <CommonBreadcumbs
                routeType={routeType}
                pageTitle={t('newWalletRequest')}
                breadcrumbs={[
                    {
                        displayText: t('walletRequests'),
                        props: {
                            href: getRoute("transactions/walletRequests")
                        }
                    },
                    {
                        displayText: t('newWalletRequest'),
                        props: {
                            color: 'secondary.main'
                        }
                    },
                ]}
            />

            {JSON.stringify(errors)}

            {
                isPermissionAllow({ functionType: functionTypes.pageNew.id }) ?
                    RenderForm()
                    :
                    <CommonRestricted />
            }

        </Box>
    )
}